import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { orgLocaleToLanguage } from './locale';

// Bridges auth state into the language context. The landing page (logged out)
// keeps device-based detection; once the user is authenticated we apply their
// account language (organization.locale). Mounted under both TranslationProvider
// and AuthProvider so it can read both contexts.
export function AccountLanguageBridge() {
  const { isAuthenticated, currentOrganization } = useAuth();
  const { setLanguage } = useTranslation();
  const appliedLocaleRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      // Reset so a future login re-applies the account language (the stored
      // preference is also cleared on logout in AuthContext).
      appliedLocaleRef.current = null;
      return;
    }

    const orgLocale = currentOrganization?.locale;
    if (!orgLocale || appliedLocaleRef.current === orgLocale) {
      return;
    }

    // Tracking the org locale (not the resolved Language) means a manual
    // in-session pick — which updates the preference but not this state — never
    // gets stomped, since the org locale value here stays unchanged.
    appliedLocaleRef.current = orgLocale;
    void setLanguage(orgLocaleToLanguage(orgLocale));
  }, [isAuthenticated, currentOrganization?.locale, setLanguage]);

  return null;
}
